import AssignmentIcon from "@mui/icons-material/Assignment";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import InfoIcon from "@mui/icons-material/Info";
import PaymentIcon from "@mui/icons-material/Payment";
import {
  Box,
  Checkbox,
  Container,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import he from "he";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import backArrow from "../../assets/icons/backArrow.svg";
import { CustomDialog, CustomTable } from "../../global/components";
import CustomLoader from "../../global/components/CustomLoader/CustomLoader";
import {
  handleErrorMessage,
  openSuccessNotification,
} from "../../helpers/methods";
import { AppPlans } from "../../models/interfaces";
import history from "../../utils/history";
import { LightTooltip, regularFont, theme } from "../../utils/styles";
import userManagementStyles from "./UserManagement.styles";
import {
  getUsersList,
  getUserSubscriptionDetails,
  updateUserStatus,
} from "./UserManagementService";

const UserManagement = () => {
  const classes = userManagementStyles;
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [userList, setUserList] = useState<any[]>([]);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [accessType, setAccessType] = useState<string>("");
  const [filteredList, setFilteredList] = useState<any[]>([]);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [myPlan, setMyPlan] = useState<AppPlans>({
    planId: "",
    priceId: "",
    price: "",
    duration: "",
    amount: 0,
    currency: "",
    name: "",
    isRecommended: false,
    details: "",
    keyFeatures: [],
    end: "",
    start: "",
  });

  const tableHeader = [
    {
      name: `${t("name")}`,
      field: "name",
    },
    {
      name: `${t("email")}`,
      field: "email",
    },
    {
      name: `${t("subscriptionStatus")}`,
      field: "subscriptionStatus",
      align: "center",
    },
    {
      name: `${t("subscriptionType")}`,
      field: "subscriptionType",
      align: "center",
    },
    {
      name: `${t("isAdmin")}`,
      field: "isAdmin",
      align: "center",
    },
    {
      name: "",
      field: "actions",
      align: "center",
    },
  ];

  useEffect(() => {
    getUserList();
  }, []);

  useEffect(() => {
    if (userList.length > 0) {
      const filtered = userList.filter((user) => {
        const searchLower = searchQuery.toLowerCase();
        const nameMatch = user.name.toLowerCase().includes(searchLower);
        const emailMatch = user.email.toLowerCase().includes(searchLower);
        return nameMatch || emailMatch;
      });
      setFilteredList(filtered);
    } else {
      setFilteredList([]);
    }
  }, [searchQuery, userList]);

  const handleAdminChange = async (email: string, isAdmin: boolean) => {
    try {
      setIsLoading(true);
      const response = await updateUserStatus({ email, isAdmin });
      openSuccessNotification(response.message);
      getUserList();
    } catch (error) {
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getUserList = async () => {
    try {
      setIsLoading(true);
      const response = await getUsersList();
      const transformedData = response.usersList.map((user: any) => {
        const fullName = `${user.firstName} ${user.lastName}`.trim();
        return {
          id: user.userId,
          name: fullName,
          email: user.email,
          subscriptionStatus: (
            <Box
              sx={{
                backgroundColor: user.subscription
                  ? "rgba(46, 204, 113, 0.15)"
                  : "rgba(231, 76, 60, 0.15)",
                color: user.subscription ? "#2ecc71" : "#e74c3c",
                borderRadius: "16px",
                padding: "6px 12px",
                display: "inline-block",
                fontSize: "18px",
                fontWeight: "500",
                ...regularFont,
              }}
            >
              {user.subscription ? t("active") : t("inactive")}
            </Box>
          ),
          subscriptionType: (
            <Typography
              sx={{
                color: theme.palette.primary.contrastText,
                opacity: user.subscription ? 1 : 0.7,
                ...regularFont,
                fontSize: "18px",
              }}
            >
              {user.subscription ? user.subscriptionType : "-"}
            </Typography>
          ),
          isAdmin: (
            <Checkbox
              checked={user.isAdmin}
              onChange={(e) => handleAdminChange(user.email, e.target.checked)}
              sx={{
                color: theme.palette.primary.contrastText,
                "&.Mui-checked": {
                  color: theme.palette.primary.contrastText,
                },
              }}
            />
          ),
          actions: user.subscription ? (
            <LightTooltip
              title={t("userInfo")}
              placement="top"
              sx={{
                "& .MuiTooltip-tooltip": {
                  backgroundColor: "#ffffff",
                  fontSize: "20px",
                  color: "#000000",
                },
              }}
            >
              <IconButton
                onClick={() => handleUserInfo(user)}
                sx={{
                  color: theme.palette.primary.contrastText,
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </LightTooltip>
          ) : (
            <></>
          ),
          searchableFields: {
            name: fullName.toLowerCase(),
            email: user.email.toLowerCase(),
          },
        };
      });
      setUserList(transformedData);
      setFilteredList(transformedData);
    } catch (error) {
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserInfo = async (user: any) => {
    try {
      setIsLoading(true);
      const language = i18n.language.toLowerCase();
      const response = await getUserSubscriptionDetails(user.userId, language);
      setMyPlan(response.planDetails);
      setAccessType(response.accessType);
      setOpenDialog(true);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getCurrentPageData = () => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredList.slice(startIndex, endIndex);
  };

  const getHeader = () => {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { sm: "center", xs: "baseline" },
          width: "100%",
        }}
        pb={3}
      >
        <Box
          display={"flex"}
          alignItems={"center"}
          sx={{ cursor: "pointer", width: "fit-content" }}
          onClick={() => history.goBack()}
        >
          {isMobile ? (
            <img src={backArrow} height={"20px"} alt="Module not found..." />
          ) : (
            <img src={backArrow} height={"25px"} alt="Module not found..." />
          )}
        </Box>
        <Typography sx={classes.italicTypo}>{t("userManagement")}</Typography>
        <TextField
          size="small"
          placeholder={t("search")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            width: "200px",
            "& .MuiOutlinedInput-root": {
              backgroundColor: "rgba(255, 255, 255, 0.14)",
              backdropFilter: "blur(24px)",
              borderRadius: "8px",
              color: theme.palette.primary.contrastText,
              "& fieldset": {
                border: "none",
              },
              "&:hover fieldset": {
                border: "none",
              },
              "&.Mui-focused fieldset": {
                border: "none",
              },
            },
            "& .MuiOutlinedInput-input": {
              "&::placeholder": {
                color: theme.palette.primary.contrastText,
                opacity: 0.7,
              },
            },
          }}
        />
      </Box>
    );
  };

  const dialogHeaderContent = () => {
    return (
      <Stack direction={"column"} alignItems={"center"} spacing={2} pt={5}>
        <Typography sx={classes.modalHeader} px={2}>
          {t("subscription")}
        </Typography>
        <Divider
          sx={{
            border: "1px solid #222222",
            width: "50%",
            height: "0px",
          }}
        />
      </Stack>
    );
  };

  const dialogBodyContent = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Stack
            direction={"row"}
            sx={classes.subscriptionDetailBox}
            spacing={2}
          >
            <PaymentIcon sx={{ color: "#8a58cb" }} />
            <Box>
              <Typography sx={classes.labelText}>{t("plan")}</Typography>
              <Typography sx={classes.valueText}>{myPlan?.name}</Typography>
            </Box>
          </Stack>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Stack
            direction={"row"}
            sx={classes.subscriptionDetailBox}
            spacing={2}
          >
            <AssignmentIcon sx={{ color: "#8a58cb" }} />
            <Box>
              <Typography sx={classes.labelText}>{t("accessType")}</Typography>
              <Typography sx={classes.valueText}>{accessType!}</Typography>
            </Box>
          </Stack>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Stack
            direction={"row"}
            sx={classes.subscriptionDetailBox}
            spacing={2}
          >
            <CalendarTodayIcon sx={{ color: "#8a58cb" }} />
            <Box>
              <Typography sx={classes.labelText}>
                {t("subscriptionPeriod")}
              </Typography>
              <Typography sx={classes.valueText}>
                {myPlan?.start} - {myPlan?.end}
              </Typography>
            </Box>
          </Stack>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Stack
            direction={"row"}
            sx={classes.subscriptionDetailBox}
            spacing={2}
          >
            <AttachMoneyIcon sx={{ color: "#8a58cb" }} />
            <Box>
              <Typography sx={classes.labelText}>{t("price")}</Typography>
              <Typography sx={classes.valueText}>
                {he.decode(myPlan?.price!)}/{myPlan?.duration}
              </Typography>
            </Box>
          </Stack>
        </Grid>
      </Grid>
    );
  };

  const getSubscriptionDialog = () => {
    return (
      <CustomDialog
        width="700px"
        borderRadius="10px"
        isDialogOpen={openDialog}
        closable={true}
        closeButtonVisibility={true}
        handleDialogClose={() => setOpenDialog(false)}
        dialogHeaderContent={dialogHeaderContent()}
        dialogBodyContent={dialogBodyContent()}
      />
    );
  };

  const getUserManagement = () => {
    return (
      <>
        <Box sx={classes.mainContainer}>
          <Box sx={classes.mainBox}>
            <Box sx={classes.subBox}>
              <Box sx={classes.scrollStyle}>
                <Container maxWidth="xl" sx={{ px: 1 }}>
                  {getHeader()}
                  <Box p={1}>
                    <Grid container>
                      <Grid
                        item
                        sm={12}
                        sx={{ overflowX: "auto", overflowY: "hidden" }}
                      >
                        <CustomTable
                          rows={getCurrentPageData()}
                          headers={tableHeader}
                          rowsPerPage={rowsPerPage}
                          paginationCount={filteredList.length}
                          pageNumber={page + 1}
                          handlePageChange={handleChangePage}
                          handlePerPageData={handleChangeRowsPerPage}
                          perPageData={rowsPerPage}
                          isRowPerPageEnable={false}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Container>
              </Box>
            </Box>
          </Box>
        </Box>
        {getSubscriptionDialog()}
        <CustomLoader isLoading={isLoading} />
      </>
    );
  };
  return getUserManagement();
};

export default UserManagement;
